import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ReCaptcha } from './ReCaptcha';
import toast from 'react-hot-toast';

function AuthModal() {
  const { 
    isAuthModalOpen, 
    closeAuthModal, 
    signInWithGoogle, 
    signInWithEmail, 
    signUpWithEmail, 
    resetPassword,
    resendVerificationEmail 
  } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  // Reset states when auth mode changes
  useEffect(() => {
    setIsVerified(false);
    setIsVerificationSent(false);
  }, [isSignUp, isForgotPassword]);

  if (!isAuthModalOpen) return null;

  const handleCaptchaChange = (token: string | null) => {
    setIsVerified(!!token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isVerified && !isForgotPassword) {
      toast.error('Please verify that you are not a robot');
      return;
    }

    try {
      if (isForgotPassword) {
        await resetPassword(email);
        toast.success('Password reset email sent');
        closeAuthModal();
        return;
      }

      if (isSignUp) {
        await signUpWithEmail(email, password, displayName);
        setIsVerificationSent(true);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (error: any) {
      if (error.message.includes('verify your email')) {
        setIsVerificationSent(true);
      }
      setError(error.message);
      setIsVerified(false); // Reset verification on error
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      toast.success('Verification email resent. Please check your inbox.');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isVerified) {
      toast.error('Please verify that you are not a robot');
      return;
    }
    try {
      await signInWithGoogle();
      closeAuthModal();
    } catch (error: any) {
      setError(error.message);
      setIsVerified(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError('');
    setIsForgotPassword(false);
    setIsVerified(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        <button
          onClick={() => {
            closeAuthModal();
            resetForm();
          }}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Sign In'}
        </h2>

        {error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
            {error}
            {isVerificationSent && (
              <button
                onClick={handleResendVerification}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Resend verification email
              </button>
            )}
          </div>
        )}

        {isVerificationSent ? (
          <div className="text-center p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Verify Your Email</h3>
            <p className="text-gray-600 mb-4">
              We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
            </p>
            <button
              onClick={handleResendVerification}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Resend verification email
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              {!isForgotPassword && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              )}

              {isSignUp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              )}

              {!isForgotPassword && (
                <div className="flex justify-center">
                  <ReCaptcha onChange={handleCaptchaChange} />
                </div>
              )}

              <button
                type="submit"
                disabled={!isVerified && !isForgotPassword}
                className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !isVerified && !isForgotPassword
                    ? 'bg-indigo-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {isForgotPassword ? 'Send Reset Link' : isSignUp ? 'Sign Up' : 'Sign In'}
              </button>
            </form>

            {!isForgotPassword && (
              <div className="mt-4">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={!isVerified}
                  className={`w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                    !isVerified
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Sign in with Google
                </button>
              </div>
            )}
          </>
        )}

        <div className="mt-4 text-center">
          {!isForgotPassword ? (
            <>
              <button
                onClick={() => {
                  setIsForgotPassword(true);
                  setError('');
                }}
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </button>
              <div className="mt-2">
                <button
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError('');
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => {
                setIsForgotPassword(false);
                setError('');
              }}
              className="text-sm text-indigo-600 hover:text-indigo-500"
            >
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;